import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE } from "../config";
import { authHeaders } from "../utils/authHeaders";
import "../styles/sales-velocity.css";

const DATE_PRESETS = [
  ["today", "Today"],
  ["last7", "Last 7 Days"],
  ["last15", "Last 15 Days"],
  ["last30", "Last 30 Days"],
  ["thisMonth", "This Month"],
  ["lastMonth", "Last Month"],
  ["custom", "Custom"],
];
const GEO_LEVELS = [
  ["state_id", "State", "states"],
  ["district_id", "District", "districts"],
  ["block_id", "Block", "blocks"],
  ["panchayat_id", "Panchayat", "panchayats"],
  ["village_id", "Village", "villages"],
];
const AGENT_SCOPE_OPTIONS = [
  ["all", "All Agents"],
  ["active", "Active in Period"],
  ["noSales", "No Sales in Period"],
  ["geography", "Geography"],
  ["custom", "Custom Selection"],
];

function indiaToday() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function shiftDate(value, days) {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function dateRangeFor(preset, today = indiaToday()) {
  const [year, month] = today.split("-").map(Number);
  if (preset === "today") return { from: today, to: today };
  if (preset === "last7") return { from: shiftDate(today, -6), to: today };
  if (preset === "last15") return { from: shiftDate(today, -14), to: today };
  if (preset === "thisMonth") return { from: `${today.slice(0, 7)}-01`, to: today };
  if (preset === "lastMonth") {
    const lastMonth = new Date(Date.UTC(year, month - 2, 1));
    const first = lastMonth.toISOString().slice(0, 10);
    const last = new Date(Date.UTC(year, month - 1, 0)).toISOString().slice(0, 10);
    return { from: first, to: last };
  }
  return { from: shiftDate(today, -29), to: today };
}

async function getJson(path) {
  const response = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.success === false) {
    const error = new Error(body.message || `Unable to load data (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return body;
}

function rowsFrom(body) {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.rows)) return body.rows;
  return [];
}

function mapWithConcurrency(items, limit, task) {
  const output = new Array(items.length);
  let next = 0;
  return Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (next < items.length) {
        const index = next++;
        output[index] = await task(items[index]);
      }
    }),
  ).then(() => output);
}

function scopePopulationKey(scope, from, to) {
  return `${scope}:${from}:${to}`;
}

function formatCurrency(value) {
  const number = Number(value);
  return Number.isFinite(number) ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number) : "—";
}

function formatQuantity(value) {
  const number = Number(value);
  return Number.isFinite(number) ? new Intl.NumberFormat("en-IN", { maximumFractionDigits: 3 }).format(number) : "—";
}

function formatVelocity(value) {
  const number = Number(value);
  return Number.isFinite(number) ? new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(number) : "—";
}

function trendPeriodLabel(point, granularity) {
  const format = (value, options) => new Intl.DateTimeFormat("en-IN", { timeZone: "UTC", ...options }).format(new Date(`${value}T00:00:00Z`));
  if (granularity === "month") return format(point.period_start, { month: "short", year: "numeric" });
  if (point.period_start === point.period_end) return format(point.period_start, { day: "numeric", month: "short" });
  return `${format(point.period_start, { day: "numeric", month: "short" })}–${format(point.period_end, { day: "numeric", month: "short" })}`;
}

function VelocityTrendChart({ series, granularity }) {
  const width = 720;
  const height = 280;
  const margin = { top: 18, right: 18, bottom: 58, left: 54 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const points = series.flatMap((item) => item.points);
  const values = points.map((point) => Number(point.calendar_velocity)).filter(Number.isFinite);
  const maximum = Math.max(1, ...values);
  const tickCount = 4;
  const palette = ["#1769aa", "#d97706", "#15803d", "#be123c", "#6d28d9", "#0f766e", "#a21caf", "#4d7c0f"];
  const x = (index, count) => margin.left + (count <= 1 ? plotWidth / 2 : index * plotWidth / (count - 1));
  const y = (value) => margin.top + plotHeight - (Number(value) / maximum) * plotHeight;
  const labels = series[0]?.points || [];
  const labelIndices = [...new Set([0, Math.floor((labels.length - 1) / 2), labels.length - 1])];
  return <div className="sv-trend-chart-wrap" role="img" aria-label={`${granularity} calendar velocity line chart, units per day`}>
    {series.length > 0 && <ul className="sv-trend-legend" aria-label="Trend series legend">{series.map((item, index) => <li key={item.agent_id ?? "combined"}><i style={{ backgroundColor: palette[index % palette.length] }} />{item.label}</li>)}</ul>}
    <svg className="sv-trend-chart" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      {[...Array(tickCount + 1)].map((_, index) => {
        const value = maximum * (tickCount - index) / tickCount;
        const yy = margin.top + (index / tickCount) * plotHeight;
        return <g key={`tick-${index}`}><line x1={margin.left} y1={yy} x2={width - margin.right} y2={yy} className="sv-chart-gridline" /><text x={margin.left - 8} y={yy + 4} textAnchor="end" className="sv-chart-axis-label">{formatVelocity(value)}</text></g>;
      })}
      <text x="13" y={margin.top + plotHeight / 2} transform={`rotate(-90 13 ${margin.top + plotHeight / 2})`} className="sv-chart-axis-title">SKU units/day</text>
      {series.map((item, seriesIndex) => {
        const path = item.points.map((point, index) => `${index ? "L" : "M"}${x(index, item.points.length)},${y(point.calendar_velocity)}`).join(" ");
        return <g key={item.agent_id ?? "combined"}>
          <path d={path} fill="none" stroke={palette[seriesIndex % palette.length]} strokeWidth="2.5" />
          {item.points.map((point, index) => <g key={point.period_start}>
            <circle cx={x(index, item.points.length)} cy={y(point.calendar_velocity)} r="4" fill={palette[seriesIndex % palette.length]} tabIndex="0" aria-label={`${trendPeriodLabel(point, granularity)}, ${item.label}, ${formatQuantity(point.units_sold)} units, ${point.calendar_days} calendar days, ${formatVelocity(point.calendar_velocity)} units/day`}>
              <title>{`${trendPeriodLabel(point, granularity)} · ${item.label} · ${formatQuantity(point.units_sold)} units · ${point.calendar_days} days · ${formatVelocity(point.calendar_velocity)} units/day`}</title>
            </circle>
          </g>)}
        </g>;
      })}
      {labelIndices.filter((index) => labels[index]).map((index) => <text key={`x-${index}`} x={x(index, labels.length)} y={height - 18} textAnchor="middle" className="sv-chart-axis-label">{trendPeriodLabel(labels[index], granularity)}</text>)}
    </svg>
    <span className="sv-trend-axis-caption">Calendar period ({granularity})</span>
  </div>;
}

function StockEfficiencyChart({ data, selectedAgentId, onSelect }) {
  const [hoveredAgentId, setHoveredAgentId] = useState(null);
  const points = data.points.filter((point) => point.current_stock !== null);
  const { product, reference } = data;
  const width = 720; const height = 300;
  const margin = { top: 24, right: 22, bottom: 58, left: 65 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const minStock = Math.min(0, ...points.map((point) => Number(point.current_stock)));
  const maxStock = Math.max(1, ...points.map((point) => Number(point.current_stock)));
  const minVelocity = Math.min(0, ...points.map((point) => Number(point.calendar_velocity)));
  const maxVelocity = Math.max(1, ...points.map((point) => Number(point.calendar_velocity)));
  const x = (value) => margin.left + (Number(value) - minStock) / (maxStock - minStock) * plotWidth;
  const y = (value) => margin.top + plotHeight - (Number(value) - minVelocity) / (maxVelocity - minVelocity) * plotHeight;
  const cover = (point) => point.cover_status === "no_recent_movement" ? "No recent movement"
    : point.cover_status === "negative_balance" ? "Unavailable — negative balance requires review"
      : `${formatQuantity(point.days_of_cover)} days`;
  const detail = (point) => `Agent: ${point.agent_name || `Agent ${point.agent_id}`}\nSKU: ${product.name} · ${product.sku || "—"}\nCurrent Operational Stock: ${formatQuantity(point.current_stock)} ${product.unit || "units"}\nCalendar Velocity: ${formatVelocity(point.calendar_velocity)} ${product.unit || "units"}/day\nUnits Sold: ${formatQuantity(point.units_sold)}\nDays of Cover: ${cover(point)}\nLast Sale: ${point.last_sale_date || "None in period"}\nStock status: ${point.stock_status === "negative_balance" ? "Negative balance — requires attention" : "Operational balance; reconciliation may be required"}`;
  const hovered = points.find((point) => point.agent_id === hoveredAgentId);
  return <div className="sv-stock-chart-wrap">
    <svg className="sv-stock-chart" viewBox={`0 0 ${width} ${height}`} aria-label="Agent stock efficiency scatter plot">
      {reference.usable && <g className="sv-stock-reference">
        <rect x={x(reference.stock_median)} y={y(reference.velocity_median)} width={x(maxStock) - x(reference.stock_median)} height={y(minVelocity) - y(reference.velocity_median)} className="sv-stock-attention-zone" />
        <line data-testid="stock-median-guide" x1={x(reference.stock_median)} x2={x(reference.stock_median)} y1={margin.top} y2={height - margin.bottom} />
        <line data-testid="velocity-median-guide" x1={margin.left} x2={width - margin.right} y1={y(reference.velocity_median)} y2={y(reference.velocity_median)} />
      </g>}
      {[0, 1, 2, 3, 4].map((index) => {
        const stock = minStock + (maxStock - minStock) * index / 4;
        const velocity = minVelocity + (maxVelocity - minVelocity) * index / 4;
        return <g key={index}>
          <line className="sv-chart-gridline" x1={margin.left} x2={width - margin.right} y1={y(velocity)} y2={y(velocity)} />
          <text className="sv-chart-axis-label" x={x(stock)} y={height - 35} textAnchor="middle">{formatQuantity(stock)}</text>
          <text className="sv-chart-axis-label" x={margin.left - 8} y={y(velocity) + 4} textAnchor="end">{formatVelocity(velocity)}</text>
        </g>;
      })}
      <text className="sv-chart-axis-title" x={width / 2} y={height - 8} textAnchor="middle">Current Operational Stock ({product.unit || "SKU units"})</text>
      <text className="sv-chart-axis-title" x="14" y={margin.top + plotHeight / 2} textAnchor="middle" transform={`rotate(-90 14 ${margin.top + plotHeight / 2})`}>Calendar Velocity (units/day)</text>
      {points.map((point) => <circle key={point.agent_id} role="button" tabIndex="0" data-agent-id={point.agent_id}
        aria-label={detail(point).replaceAll("\n", ", ")} aria-pressed={point.agent_id === selectedAgentId}
        className={`sv-stock-point${point.agent_id === selectedAgentId ? " is-selected" : ""}${point.stock_status === "negative_balance" ? " is-negative" : ""}`}
        cx={x(point.current_stock)} cy={y(point.calendar_velocity)} r={point.agent_id === selectedAgentId ? "7" : "5"}
        onMouseEnter={() => setHoveredAgentId(point.agent_id)} onMouseLeave={() => setHoveredAgentId(null)}
        onFocus={() => setHoveredAgentId(point.agent_id)} onBlur={() => setHoveredAgentId(null)}
        onClick={() => onSelect(point.agent_id)} onKeyDown={(event) => { if (["Enter", " "].includes(event.key)) { event.preventDefault(); onSelect(point.agent_id); } }}>
        <title>{detail(point)}</title>
      </circle>)}
    </svg>
    {hovered && <div className="sv-stock-tooltip" role="status">{detail(hovered)}</div>}
    <p className="sv-muted sv-stock-reference-note">{reference.usable
      ? "Attention: higher stock / lower movement. Quadrants are relative to the selected Agent population."
      : "Insufficient variation for relative zones."}</p>
  </div>;
}

function compareNumericText(left, right) {
  const a = String(left); const b = String(right);
  const matchA = /^(-?)(\d+)(?:\.(\d+))?$/.exec(a);
  const matchB = /^(-?)(\d+)(?:\.(\d+))?$/.exec(b);
  if (!matchA || !matchB) return Number(left) - Number(right);
  const negativeA = Boolean(matchA[1]); const negativeB = Boolean(matchB[1]);
  if (negativeA !== negativeB) return negativeA ? -1 : 1;
  const integerA = matchA[2].replace(/^0+/, "") || "0";
  const integerB = matchB[2].replace(/^0+/, "") || "0";
  let magnitude = integerA.length === integerB.length
    ? integerA.localeCompare(integerB)
    : integerA.length < integerB.length ? -1 : 1;
  if (magnitude === 0) {
    const scale = Math.max(matchA[3]?.length || 0, matchB[3]?.length || 0);
    const fractionA = (matchA[3] || "").padEnd(scale, "0");
    const fractionB = (matchB[3] || "").padEnd(scale, "0");
    magnitude = fractionA.localeCompare(fractionB);
  }
  return negativeA ? -magnitude : magnitude;
}

function rankAgents(points, order, agents) {
  const direction = order === "fastest" ? -1 : 1;
  const nameById = new Map(agents.map((agent) => [String(agent.id), agent.name || `Agent ${agent.id}`]));
  const sorted = [...points].sort((left, right) => direction * compareNumericText(left.calendar_velocity, right.calendar_velocity)
    || (nameById.get(String(left.agent_id)) || left.agent_name || "").localeCompare(nameById.get(String(right.agent_id)) || right.agent_name || "", undefined, { sensitivity: "base" })
    || String(left.agent_id).localeCompare(String(right.agent_id), undefined, { numeric: true }));
  let rank = 0; let previousVelocity = null;
  return sorted.map((point) => {
    const velocity = String(point.calendar_velocity);
    if (previousVelocity === null || compareNumericText(velocity, previousVelocity) !== 0) rank += 1;
    previousVelocity = velocity;
    return { ...point, rank, displayName: nameById.get(String(point.agent_id)) || point.agent_name || `Agent ${point.agent_id}` };
  });
}

function AgentRankingTable({ points, agents, order, limit, selectedAgentId, onSelect }) {
  if (!points) return <p className="sv-chart-status" role="status">Loading Agent ranking…</p>;
  if (!points.length) return <div className="sv-empty-state" role="status"><p>No Agents match the applied filters.</p></div>;
  const ranked = rankAgents(points, order, agents);
  return <div className="sv-ranking-table-scroll"><table className="sv-ranking-table" aria-label="Agent movement ranking">
    <thead><tr>{["Rank", "Agent", "SKU Units Sold", "Velocity", "Current Operational Stock", "Days of Cover", "Last Sale"].map((label) => <th key={label} scope="col">{label}</th>)}</tr></thead>
    <tbody>{ranked.slice(0, limit).map((point) => <tr key={point.agent_id} className={String(point.agent_id) === String(selectedAgentId) ? "is-selected" : ""}>
      <td>{point.rank}</td>
      <th scope="row"><button type="button" className="sv-ranking-agent" aria-pressed={String(point.agent_id) === String(selectedAgentId)} onClick={() => onSelect(point.agent_id)}>{point.displayName}</button></th>
      <td>{formatQuantity(point.units_sold)}</td>
      <td>{formatVelocity(point.calendar_velocity)}</td>
      <td>{point.current_stock === null ? "Unavailable" : <span className={point.stock_status === "negative_balance" ? "sv-negative-stock" : ""}>{formatQuantity(point.current_stock)}{point.stock_status === "negative_balance" ? " · Attention" : ""}</span>}</td>
      <td>{point.cover_status === "stock_unavailable" ? "Unavailable" : point.cover_status === "negative_balance" ? "Unavailable · Negative balance" : point.cover_status === "no_recent_movement" ? "No recent movement" : `${formatQuantity(point.days_of_cover)} days`}</td>
      <td>{point.last_sale_date || "No sales in period"}</td>
    </tr>)}</tbody>
  </table></div>;
}

function SkuMovementTable({ rows, selectedSkuId, onFocusSku }) {
  if (!rows?.length) return <div className="sv-empty-state" role="status"><p>Select one or more SKUs to view the movement summary.</p></div>;
  return <>
    <p className="sv-sku-comparability">Each SKU is measured in its own saleable unit; velocity values across different SKUs are not weight-normalised.</p>
    <div className="sv-ranking-table-scroll"><table className="sv-ranking-table sv-sku-summary-table" aria-label="SKU movement summary">
      <thead><tr>{["SKU / Product", "SKU Units Sold", "Calendar Velocity", "Active Agents", "Known Operational Stock", "Stock Availability", "Days of Cover", "Last Sale"].map((label) => <th key={label} scope="col">{label}</th>)}</tr></thead>
      <tbody>{rows.map((row) => <tr key={row.product_id} className={String(row.product_id) === String(selectedSkuId) ? "is-selected" : ""}>
        <th scope="row"><button type="button" className="sv-ranking-agent" aria-pressed={String(row.product_id) === String(selectedSkuId)} onClick={() => onFocusSku(row.product_id)}>{row.product_name || row.sku || `Product ${row.product_id}`}{row.sku ? <small>{row.sku}</small> : null}</button></th>
        <td>{formatQuantity(row.units_sold)} {row.unit || "units"}</td>
        <td>{formatVelocity(row.calendar_velocity)} {row.unit || "units"}/day</td>
        <td>{row.active_agents}</td>
        <td>{row.known_current_stock === null ? "Unavailable" : <span className={Number(row.known_current_stock) < 0 ? "sv-negative-stock" : ""}>{formatQuantity(row.known_current_stock)} {row.unit || "units"}{row.negative_stock_agents > 0 ? ` · ${row.negative_stock_agents} negative` : ""}</span>}</td>
        <td>{row.stock_availability_status === "complete" ? `${row.stock_known_agents} of ${row.eligible_agents} Agents` : row.stock_availability_status === "partial" ? `${row.stock_known_agents} of ${row.eligible_agents} Agents · Partial` : "No Agents"}</td>
        <td>{row.cover_status === "partial_stock" ? "Unavailable · Partial stock" : row.cover_status === "no_recent_movement" ? "No recent movement" : row.cover_status === "invalid_operational_balance" ? "Unavailable · Negative balance" : row.cover_status === "no_agents" ? "Unavailable" : `${formatQuantity(row.days_of_cover)} days`}</td>
        <td>{row.last_sale_date || "No sales in period"}</td>
      </tr>)}</tbody>
    </table></div>
  </>;
}

function PerformanceRanking({ view, setView, rankingOrder, setRankingOrder, rankingLimit, setRankingLimit,
  rankingPoints, rankingLoading, rankingError, onRetryRanking, agents, selectedAgentId, focusedSku,
  onSelectAgent, skuRows, skuSummaryError, skuSummaryLoading, onRetrySkuSummary, selectedSkuId, onFocusSku }) {
  return <>
    <div className="sv-ranking-controls" role="group" aria-label="Performance ranking view">
      <button type="button" aria-pressed={view === "agents"} onClick={() => setView("agents")}>Agents for Focused SKU</button>
      <button type="button" aria-pressed={view === "skus"} onClick={() => setView("skus")}>SKU Movement Summary</button>
    </div>
    {view === "agents" ? <>
      {!focusedSku ? <div className="sv-empty-state" role="status"><p>Select a SKU to rank Agent movement.</p></div> : <>
        <p className="sv-ranking-focused">Focused SKU: <strong>{focusedSku.name || focusedSku.sku}</strong></p>
        <div className="sv-ranking-subcontrols">
          <div className="sv-ranking-controls" role="group" aria-label="Agent ranking order">
            <button type="button" aria-pressed={rankingOrder === "fastest"} onClick={() => setRankingOrder("fastest")}>Fastest</button>
            <button type="button" aria-pressed={rankingOrder === "slowest"} onClick={() => setRankingOrder("slowest")}>Slowest</button>
          </div>
          <div className="sv-ranking-controls" role="group" aria-label="Number of ranked Agents">
            {[10, 20].map((count) => <button key={count} type="button" aria-pressed={rankingLimit === count} onClick={() => setRankingLimit(count)}>Show {count}</button>)}
          </div>
        </div>
        {rankingError ? <div className="sv-error" role="alert"><span>{rankingError}</span><button type="button" className="sv-inline-action" onClick={onRetryRanking}>Retry ranking</button></div>
          : rankingLoading ? <p className="sv-chart-status" role="status">Loading Agent ranking…</p>
            : <AgentRankingTable points={rankingPoints} agents={agents} order={rankingOrder} limit={rankingLimit} selectedAgentId={selectedAgentId} onSelect={onSelectAgent} />}
        <p className="sv-ranking-note">Dense ranks compare Calendar Velocity for this SKU across the full applied Agent population. Equal velocities share a rank; Agent names break display ties.</p>
      </>}
    </> : skuSummaryError ? <div className="sv-error" role="alert"><span>{skuSummaryError}</span><button type="button" className="sv-inline-action" onClick={onRetrySkuSummary}>Retry SKU summary</button></div>
      : skuSummaryLoading ? <p className="sv-chart-status" role="status">Loading SKU movement summary…</p>
        : <SkuMovementTable rows={skuRows} selectedSkuId={selectedSkuId} onFocusSku={onFocusSku} />}
  </>;
}

function velocityBand(value, benchmark) {
  if (!benchmark || !Number.isFinite(Number(value))) return "unavailable";
  if (benchmark.classification_mode === "neutral") return "equal";
  const velocity = Number(value);
  if (velocity < Number(benchmark.lower_threshold)) return "low";
  if (velocity > Number(benchmark.upper_threshold)) return "high";
  return "middle";
}

function MultiSelect({ label, options, selected, onChange, searchPlaceholder, testId, compactCount = false, disabled = false, disabledSummary = "Unavailable" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return normalized
      ? options.filter((option) => option.searchText.toLowerCase().includes(normalized))
      : options;
  }, [options, query]);
  const selectedOptions = options.filter((option) => selected.has(String(option.id)));
  const summary = disabled
    ? disabledSummary
    : compactCount
      ? `${selectedOptions.length} of ${options.length} selected`
      : selectedOptions.length
        ? `${selectedOptions.slice(0, 3).map((option) => option.name).join(", ")}${selectedOptions.length > 3 ? ` +${selectedOptions.length - 3} more` : ""}`
        : "None selected";

  function toggle(id) {
    const next = new Set(selected);
    if (next.has(String(id))) next.delete(String(id));
    else next.add(String(id));
    onChange(next);
  }

  return (
    <div className="sv-multiselect" data-testid={testId}>
      <span className="sv-filter-label">{label}</span>
      <button type="button" className="sv-select-trigger" aria-expanded={open} disabled={disabled}
        aria-label={compactCount ? `${label}: ${summary}` : `${label}: ${selected.size} selected`} onClick={() => setOpen((value) => !value)}>
        <span className="sv-select-summary">{summary}</span>
        <span aria-hidden="true">{open ? "▴" : "▾"}</span>
      </button>
      {open && !disabled && (
        <div className="sv-select-menu">
          <input aria-label={searchPlaceholder} placeholder={searchPlaceholder} value={query}
            onChange={(event) => setQuery(event.target.value)} />
          <div className="sv-select-actions">
            <button type="button" onClick={() => onChange(new Set([...selected, ...filtered.map((item) => String(item.id))]))}>Select all filtered</button>
            <button type="button" onClick={() => onChange(new Set())}>Clear</button>
          </div>
          <div className="sv-select-options">
            {filtered.map((option) => (
              <label key={option.id} className="sv-option">
                <input type="checkbox" checked={selected.has(String(option.id))} onChange={() => toggle(option.id)} />
                <span><strong>{option.name}</strong>{option.detail && <small>{option.detail}</small>}</span>
              </label>
            ))}
            {filtered.length === 0 && <p className="sv-muted">No matches.</p>}
          </div>
          <small className="sv-selection-count">Selected {selected.size} of {options.length}</small>
        </div>
      )}
    </div>
  );
}

function DashboardSwitcher() {
  return (
    <nav className="sv-switcher" aria-label="Dashboards">
      <Link to="/dashboard">FPO Overview</Link>
      <span aria-hidden="true">/</span>
      <Link to="/dashboard/sales-velocity" aria-current="page">Sales Velocity</Link>
    </nav>
  );
}

function DetailMovementChart({ days, unit }) {
  if (!days.length) return <div className="sv-empty-state" role="status"><p>No daily movement is available for this period.</p></div>;
  const width = Math.max(640, days.length * 14);
  const height = 180;
  const top = 18;
  const bottom = 30;
  const plotHeight = height - top - bottom;
  const max = Math.max(1, ...days.map((day) => Number(day.unitsSold) || 0));
  const slot = width / days.length;
  const barWidth = Math.max(2, Math.min(9, slot * 0.65));
  const labelStep = Math.max(1, Math.ceil(days.length / 10));
  return <div className="sv-detail-chart-scroll" role="region" aria-label="Daily movement chart" tabIndex="0">
    <svg className="sv-detail-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Daily SKU units sold, ${unit || "units"}`}>
      <line x1="0" x2={width} y1={top + plotHeight} y2={top + plotHeight} className="sv-chart-gridline" />
      {days.map((day, index) => {
        const value = Number(day.unitsSold) || 0;
        const barHeight = value > 0 ? Math.max(2, value / max * plotHeight) : 1;
        const x = index * slot + (slot - barWidth) / 2;
        const y = top + plotHeight - barHeight;
        return <g key={day.date}>
          <title>{`${day.date} · ${day.unitsSold} ${unit || "units"} · ${day.transactionCount} Sales transaction${day.transactionCount === 1 ? "" : "s"}`}</title>
          <rect x={x} y={y} width={barWidth} height={barHeight} rx="1" className={value === 0 ? "sv-detail-bar is-zero" : "sv-detail-bar"} />
          {index % labelStep === 0 && <text x={index * slot + slot / 2} y={height - 8} textAnchor="middle" className="sv-chart-axis-label">{day.date.slice(8, 10)}</text>}
        </g>;
      })}
    </svg>
  </div>;
}

function displayCover(summary) {
  if (summary.coverStatus === "stock_unavailable") return "Stock unavailable";
  if (summary.coverStatus === "negative_balance") return "Negative balance";
  if (summary.coverStatus === "no_recent_movement") return "No recent movement";
  return summary.daysOfCover === null ? "Unavailable" : `${formatQuantity(summary.daysOfCover)} days`;
}

function AnalyticsPlaceholder({ title, children, className = "", loading = false }) {
  return (
    <section className={`sv-panel ${className}`}>
      <h2>{title}</h2>
      {loading ? <p className="sv-chart-status" role="status">Updating applied filters…</p> : children}
    </section>
  );
}

export default function SalesVelocity() {
  const initialDates = dateRangeFor("last30");
  const [agents, setAgents] = useState([]);
  const [products, setProducts] = useState([]);
  const [geo, setGeo] = useState({ states: [], districts: [], blocks: [], panchayats: [], villages: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [agentMappingError, setAgentMappingError] = useState("");
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingLoadAttempted, setMappingLoadAttempted] = useState(false);
  const [agentMappings, setAgentMappings] = useState(null);

  const [period, setPeriod] = useState("last30");
  const [from, setFrom] = useState(initialDates.from);
  const [to, setTo] = useState(initialDates.to);
  const [scope, setScope] = useState("all");
  const [selectedAgents, setSelectedAgents] = useState(new Set());
  const [geoSelection, setGeoSelection] = useState({ state_id: "", district_id: "", block_id: "", panchayat_id: "", village_id: "" });
  const [category, setCategory] = useState("");
  const [selectedSkus, setSelectedSkus] = useState(new Set());
  const [dateError, setDateError] = useState("");
  const [appliedContext, setAppliedContext] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [resolvedScopePopulation, setResolvedScopePopulation] = useState(null);
  const [appliedQuery, setAppliedQuery] = useState(null);
  const [agentSort, setAgentSort] = useState("agent_name");
  const [selectedPair, setSelectedPair] = useState(null);
  const [detailState, setDetailState] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailRetry, setDetailRetry] = useState(0);
  const [detailSalesPage, setDetailSalesPage] = useState(1);
  const [detailSalesPageSize, setDetailSalesPageSize] = useState(10);
  const [focusedSkuId, setFocusedSkuId] = useState("");
  const [trendGranularity, setTrendGranularity] = useState("day");
  const [trendMode, setTrendMode] = useState("combined");
  const [trendModeExplicit, setTrendModeExplicit] = useState(false);
  const [trendAgentIds, setTrendAgentIds] = useState(new Set());
  const [trendEligibleAgentIds, setTrendEligibleAgentIds] = useState([]);
  const [trendGlobalQuery, setTrendGlobalQuery] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState(null);
  const [trendAgentSearch, setTrendAgentSearch] = useState("");
  const [trendRetry, setTrendRetry] = useState(0);
  const [stockData, setStockData] = useState(null);
  const [stockError, setStockError] = useState(null);
  const [stockRetry, setStockRetry] = useState(0);
  const [rankingView, setRankingView] = useState("agents");
  const [rankingOrder, setRankingOrder] = useState("fastest");
  const [rankingLimit, setRankingLimit] = useState(10);
  const [skuSummaryRetryData, setSkuSummaryRetryData] = useState(null);
  const [skuSummaryRetryError, setSkuSummaryRetryError] = useState(null);
  const [skuSummaryRetryLoading, setSkuSummaryRetryLoading] = useState(false);

  useEffect(() => {
    let active = true;
    async function loadFilters() {
      setLoading(true);
      const requests = [
        ["agents", "/agents"],
        ["products", "/products/list"],
        ["states", "/fpo-geo/states"],
        ["districts", "/fpo-geo/districts"],
        ["blocks", "/fpo-geo/blocks"],
        ["panchayats", "/fpo-geo/panchayats"],
        ["villages", "/fpo-geo/villages"],
      ];
      const results = await Promise.allSettled(requests.map(([, path]) => getJson(path)));
      if (!active) return;
      const errors = [];
      const nextGeo = { states: [], districts: [], blocks: [], panchayats: [], villages: [] };
      results.forEach((result, index) => {
        const [key] = requests[index];
        if (result.status === "fulfilled") {
            const data = rowsFrom(result.value);
            if (key === "agents") {
              const eligible = data.filter((agent) => agent.is_active === true && agent.sales_module_enabled === true);
              setAgents(eligible);
              setSelectedAgents(new Set(eligible.map((agent) => String(agent.id))));
            } else if (key === "products") setProducts(data);
            else nextGeo[key] = data;
        } else {
          errors.push(`${key}: ${result.reason?.message || "failed"}`);
        }
      });
      setGeo(nextGeo);
      if (errors.length) setLoadError(`Some filter data could not be loaded: ${errors.join("; ")}`);
      setLoading(false);
    }
    loadFilters();
    return () => { active = false; };
  }, []);

  const categories = useMemo(() => [...new Set(products.map((product) => product.category).filter(Boolean))].sort(), [products]);
  const categoryProducts = useMemo(() => category ? products.filter((product) => product.category === category) : products, [category, products]);
  const skuOptions = useMemo(() => categoryProducts.map((product) => ({
    id: product.id,
    name: product.name || product.sku || `Product ${product.id}`,
    detail: `SKU: ${product.sku || "—"} · Unit: ${product.unit || "—"}`,
    searchText: `${product.name || ""} ${product.sku || ""} ${product.unit || ""}`,
  })), [categoryProducts]);
  const agentOptions = useMemo(() => agents.map((agent) => ({
    id: agent.id,
    name: agent.name || `Agent ${agent.id}`,
    detail: agent.is_active === false ? "Inactive" : "Active",
    searchText: `${agent.name || ""} ${agent.phone || ""} ${agent.village || ""}`,
  })), [agents]);

  const selectedGeoVillages = useMemo(() => {
    const fieldDepth = GEO_LEVELS.findIndex(([field]) => geoSelection[field]);
    if (fieldDepth < 0) return [];
    const byId = Object.fromEntries(geo.villages.map((village) => [String(village.id), village]));
    return geo.villages.filter((village) => {
      let current = byId[String(village.id)];
      if (!current) return false;
      if (geoSelection.village_id) return String(village.id) === geoSelection.village_id;
      if (geoSelection.panchayat_id && String(village.panchayat_id) !== geoSelection.panchayat_id) return false;
      const panchayat = geo.panchayats.find((item) => String(item.id) === String(current.panchayat_id));
      if (geoSelection.block_id && String(panchayat?.block_id) !== geoSelection.block_id) return false;
      const block = geo.blocks.find((item) => String(item.id) === String(panchayat?.block_id));
      if (geoSelection.district_id && String(block?.district_id) !== geoSelection.district_id) return false;
      const district = geo.districts.find((item) => String(item.id) === String(block?.district_id));
      if (geoSelection.state_id && String(district?.state_id) !== geoSelection.state_id) return false;
      return true;
    });
  }, [geo, geoSelection]);

  const geographyAgents = useMemo(() => {
    if (scope !== "geography") return agents;
    if (!agentMappings) return [];
    const mappedAgentIds = new Set(agents
      .filter((agent) => (agentMappings[String(agent.id)] || []).length > 0)
      .map((agent) => String(agent.id)));
    if (selectedGeoVillages.length === 0) return agents.filter((agent) => mappedAgentIds.has(String(agent.id)));
    const selectedVillageIds = new Set(selectedGeoVillages.map((village) => String(village.id)));
    return agents.filter((agent) => {
      const mapped = agentMappings[String(agent.id)] || [];
      return mapped.some((id) => selectedVillageIds.has(String(id)));
    });
  }, [agentMappings, agents, scope, selectedGeoVillages]);
  const selectableAgents = scope === "geography" ? geographyAgents : agents;
  const selectableAgentOptions = useMemo(() => {
    const allowed = new Set(selectableAgents.map((agent) => String(agent.id)));
    return agentOptions.filter((option) => allowed.has(String(option.id)));
  }, [agentOptions, selectableAgents]);

  function updateGeo(field, value) {
    const depth = GEO_LEVELS.findIndex(([key]) => key === field);
    setGeoSelection((current) => {
      const next = { ...current, [field]: value };
      for (let index = depth + 1; index < GEO_LEVELS.length; index += 1) next[GEO_LEVELS[index][0]] = "";
      return next;
    });
  }

  useEffect(() => {
    if (scope === "all") {
      setSelectedAgents(new Set(agents.map((agent) => String(agent.id))));
      return;
    }
    if (scope === "geography" && agentMappings) {
      setSelectedAgents(new Set(geographyAgents.map((agent) => String(agent.id))));
    }
  }, [agents, scope, agentMappings, geographyAgents]);

  const loadAgentMappings = useCallback(async () => {
    if (agentMappings || mappingLoading || mappingLoadAttempted || agents.length === 0) return;
    setMappingLoadAttempted(true);
    setMappingLoading(true);
    setAgentMappingError("");
    try {
      const pairs = await mapWithConcurrency(agents, 6, async (agent) => {
        const body = await getJson(`/agent-geo-mapping/${encodeURIComponent(agent.id)}`);
        return [String(agent.id), Array.isArray(body.mapped_village_ids) ? body.mapped_village_ids : []];
      });
      setAgentMappings(Object.fromEntries(pairs));
    } catch (error) {
      setAgentMappingError(error.message || "Agent geography mappings could not be loaded.");
    } finally {
      setMappingLoading(false);
    }
  }, [agentMappings, mappingLoading, mappingLoadAttempted, agents]);

  function retryAgentMappings() {
    setAgentMappings(null);
    setMappingLoadAttempted(false);
    setAgentMappingError("");
  }

  function changeScope(value) {
    setScope(value);
    if (value === "all") setSelectedAgents(new Set(agents.map((agent) => String(agent.id))));
    else setSelectedAgents(new Set());
  }

  useEffect(() => {
    if (scope === "geography" && agents.length > 0 && !agentMappings && !mappingLoading && !mappingLoadAttempted) {
      loadAgentMappings();
    }
  }, [scope, agents, agentMappings, mappingLoading, mappingLoadAttempted, loadAgentMappings]);

  function updatePeriod(value) {
    setPeriod(value);
    if (value !== "custom") {
      const dates = dateRangeFor(value);
      setFrom(dates.from);
      setTo(dates.to);
    }
    setDateError("");
  }

  async function loadAnalytics(query, context = null) {
    setAnalyticsLoading(true);
    if (context) setContextLoading(true);
    setAnalyticsError(null);
    try {
      const result = await getJson(`/analytics/sales-velocity?${query.toString()}`);
      setAnalytics(result);
      setAppliedQuery(query.toString());
      setSkuSummaryRetryData(null);
      setSkuSummaryRetryError(null);
      const matchingAgentIds = (result.filters?.agent_ids || []).map(String);
      const requestedSkuIds = JSON.parse(query.get("product_ids") || "[]").map(String);
      setSelectedPair((current) => current
        && matchingAgentIds.includes(String(current.agentId))
        && requestedSkuIds.includes(String(current.productId)) ? current : null);
      if (context) {
        setDetailSalesPage(1);
        const skuIds = JSON.parse(query.get("product_ids") || "[]").map(String);
        const eligibleIds = (result.filters?.agent_ids || []).map(String);
        setTrendData(null);
        setTrendError(null);
        setTrendEligibleAgentIds(eligibleIds);
        setTrendGlobalQuery(query.toString());
        setFocusedSkuId((current) => current && skuIds.includes(current) ? current : skuIds[0] || "");
        setTrendAgentIds((current) => {
          const retained = new Set([...current].filter((id) => eligibleIds.includes(id)));
          if (retained.size || eligibleIds.length > 5) return retained;
          return new Set(eligibleIds);
        });
        if (!trendModeExplicit) setTrendMode(eligibleIds.length > 5 ? "combined" : "agents");
        if (["active", "noSales"].includes(context.scope)) {
          setResolvedScopePopulation({ key: context.key, agentIds: (result.population?.available_agent_ids || []).map(String) });
          if (!context.resolved) setSelectedAgents(new Set((result.filters?.agent_ids || []).map(String)));
        }
        const selectedCount = result.population?.selected_agent_count ?? 0;
        setAppliedContext({
          period: DATE_PRESETS.find(([preset]) => preset === context.period)?.[1] || "Custom",
          from: context.from,
          to: context.to,
          scope: context.scope,
          agentCount: selectedCount,
          matchingAgentCount: result.population?.matching_agent_count ?? 0,
          category: context.category || "All categories",
          skuCount: context.skuCount,
        });
      }
    } catch (error) {
      setAnalyticsError({
        status: error.status,
        message: error.status === 401
          ? "Your Admin session is no longer valid. Sign in again to view analytics."
          : error.status === 403
            ? "Your account does not have permission to view Sales Velocity analytics."
            : error.message || "Sales Velocity analytics could not be loaded.",
      });
    } finally {
      setAnalyticsLoading(false);
      if (context) setContextLoading(false);
    }
  }

  async function applyFilters() {
    if (!from || !to || from > to) {
      setDateError("Choose a valid date range: From must be on or before To.");
      return;
    }
    if (scope === "geography" && !Object.values(geoSelection).some(Boolean)) {
      setDateError("Choose at least one geography level before applying.");
      return;
    }
    setDateError("");
    const key = scopePopulationKey(scope, from, to);
    const resolved = ["active", "noSales"].includes(scope) && resolvedScopePopulation?.key === key;
    const query = new URLSearchParams({
      from,
      to,
      agent_scope: scope === "active" ? "active_in_period" : scope === "noSales" ? "no_sales_in_period" : scope,
      product_ids: JSON.stringify([...selectedSkus]),
      categories: JSON.stringify(category ? [category] : []),
      page: "1",
      page_size: String(analytics?.agentSku?.pageSize || 10),
      sort_by: agentSort,
    });
    if (!( ["active", "noSales"].includes(scope) && !resolved)) query.set("agent_ids", JSON.stringify([...selectedAgents]));
    for (const [field, value] of Object.entries(geoSelection)) if (value) query.set(field, value);
    await loadAnalytics(query, { key, resolved, period, from, to, scope, category, skuCount: selectedSkus.size });
  }

  async function requestAnalyticsPage(page, pageSize = analytics?.agentSku?.pageSize || 10) {
    if (!appliedQuery) return;
    const query = new URLSearchParams(appliedQuery);
    query.set("page", String(page));
    query.set("page_size", String(pageSize));
    await loadAnalytics(query);
  }

  function resetFilters() {
    const dates = dateRangeFor("last30");
    setPeriod("last30");
    setFrom(dates.from);
    setTo(dates.to);
    setScope("all");
    setSelectedAgents(new Set(agents.map((agent) => String(agent.id))));
    setGeoSelection({ state_id: "", district_id: "", block_id: "", panchayat_id: "", village_id: "" });
    setCategory("");
    setSelectedSkus(new Set());
    setDateError("");
    setAnalytics(null);
    setAnalyticsError(null);
    setResolvedScopePopulation(null);
    setAppliedContext(null);
    setAppliedQuery(null);
    setSelectedPair(null);
    setAgentSort("agent_name");
    setFocusedSkuId("");
    setTrendAgentIds(new Set());
    setTrendEligibleAgentIds([]);
    setTrendGlobalQuery(null);
    setTrendData(null);
    setTrendError(null);
    setTrendMode("combined");
    setTrendModeExplicit(false);
    setStockData(null);
    setStockError(null);
  }

  const agentPopulationUnavailable = scope === "active" || scope === "noSales";
  const activePopulationKey = scopePopulationKey(scope, from, to);
  const activePopulationResolved = !agentPopulationUnavailable || resolvedScopePopulation?.key === activePopulationKey;
  const agentControlDisabled = (agentPopulationUnavailable && !activePopulationResolved) || loading || (scope === "geography" && (!agentMappings || mappingLoading || Boolean(agentMappingError)));
  const agentUnavailableSummary = agentPopulationUnavailable && !activePopulationResolved
    ? "Available when analytics is applied"
    : loading
      ? "Loading Agents…"
      : scope === "geography" && agentMappingError
        ? "Geography population unavailable"
        : "Loading mapped Agents…";
  const activePopulationIds = new Set(resolvedScopePopulation?.key === activePopulationKey ? resolvedScopePopulation.agentIds : []);
  const visibleAgentOptions = agentPopulationUnavailable
    ? activePopulationResolved ? agentOptions.filter((option) => activePopulationIds.has(String(option.id))) : []
    : selectableAgentOptions;
  const kpis = [
    ["Sales Value", analytics ? formatCurrency(analytics.kpis.sales_value) : "—", analytics ? "Valid Sales" : "Apply filters to calculate"],
    ["SKU Units Sold", analytics ? formatQuantity(analytics.kpis.sku_units_sold) : "—", analytics ? "Item quantity" : "Apply filters to calculate"],
    ["Active Agents", analytics ? String(analytics.kpis.active_agents) : "—", analytics ? "With matching Sales" : "Apply filters to calculate"],
    ["Active SKUs", analytics ? String(analytics.kpis.active_skus) : "—", analytics ? "With matching Sales" : "Apply filters to calculate"],
  ];
  const appliedSkuIds = appliedQuery ? JSON.parse(new URLSearchParams(appliedQuery).get("product_ids") || "[]").map(String) : [];
  const heatmapProducts = products.filter((product) => appliedSkuIds.includes(String(product.id)));
  const heatmapRows = analytics?.agentSku?.rows || [];
  const pageAgentIds = analytics?.agentSku?.agentIds || [...new Set(heatmapRows.map((row) => String(row.agent_id)))];
  const agentById = new Map(agents.map((agent) => [String(agent.id), agent]));
  const productById = new Map(products.map((product) => [String(product.id), product]));
  const focusedProductId = focusedSkuId || appliedSkuIds[0] || "";
  const focusedProduct = productById.get(String(focusedProductId));
  const summaryRetryIsCurrent = skuSummaryRetryData?.key === appliedQuery;
  const skuSummaryRows = summaryRetryIsCurrent ? skuSummaryRetryData.rows : analytics?.skuSummary || [];
  const skuSummaryError = skuSummaryRetryError || (!summaryRetryIsCurrent ? analytics?.skuSummaryError : null);
  const benchmarkByProduct = new Map((analytics?.agentSku?.skuBenchmarks || []).map((benchmark) => [String(benchmark.product_id), benchmark]));
  const rowByPair = new Map(heatmapRows.map((row) => [`${row.agent_id}:${row.product_id}`, row]));
  const totalHeatmapAgents = Number(analytics?.agentSku?.totalAgents || 0);
  const heatmapPageSize = Number(analytics?.agentSku?.pageSize || 10);
  const heatmapPage = Number(analytics?.agentSku?.page || 1);
  const heatmapPageCount = Math.max(1, Math.ceil(totalHeatmapAgents / heatmapPageSize));
  const trendEligibleAgents = trendEligibleAgentIds.map((id) => agentById.get(String(id))).filter(Boolean);
  const trendAgentOptions = trendEligibleAgents.filter((agent) => `${agent.name || ""} ${agent.phone || ""}`.toLowerCase().includes(trendAgentSearch.trim().toLowerCase()));

  useEffect(() => {
    if (!trendGlobalQuery || !focusedSkuId) {
      setTrendData(null);
      setTrendLoading(false);
      return undefined;
    }
    if (trendMode === "agents" && trendAgentIds.size === 0) {
      setTrendData(null);
      setTrendLoading(false);
      return undefined;
    }
    let current = true;
    const query = new URLSearchParams(trendGlobalQuery);
    query.delete("page");
    query.delete("page_size");
    query.delete("sort_by");
    query.set("agent_ids", JSON.stringify(trendEligibleAgentIds));
    query.set("product_id", focusedSkuId);
    query.set("granularity", trendGranularity);
    query.set("mode", trendMode);
    if (trendMode === "agents") query.set("series_agent_ids", JSON.stringify([...trendAgentIds]));
    else query.delete("series_agent_ids");
    setTrendLoading(true);
    setTrendError(null);
    getJson(`/analytics/sales-velocity/trend?${query.toString()}`)
      .then((result) => { if (current) setTrendData(result); })
      .catch((error) => { if (current) setTrendError(error.message || "Sales Velocity trend could not be loaded."); })
      .finally(() => { if (current) setTrendLoading(false); });
    return () => { current = false; };
  }, [trendAgentIds, trendEligibleAgentIds, trendGlobalQuery, trendGranularity, trendMode, trendRetry, focusedSkuId]);

  const stockRequestKey = trendGlobalQuery && focusedSkuId ? `${trendGlobalQuery}|${focusedSkuId}` : null;
  const currentStockData = stockData?.key === stockRequestKey ? stockData.result : null;
  const currentStockError = stockError?.key === stockRequestKey ? stockError.message : null;
  useEffect(() => {
    if (!stockRequestKey || trendEligibleAgentIds.length === 0) return undefined;
    let current = true;
    const query = new URLSearchParams(trendGlobalQuery);
    for (const field of ["page", "page_size", "sort_by"]) query.delete(field);
    query.set("product_id", focusedSkuId);
    query.set("agent_ids", JSON.stringify(trendEligibleAgentIds));
    setStockData(null);
    setStockError(null);
    getJson(`/analytics/sales-velocity/stock-efficiency?${query.toString()}`)
      .then((result) => { if (current) setStockData({ key: stockRequestKey, result }); })
      .catch((error) => { if (current) setStockError({ key: stockRequestKey, message: error.message || "Stock efficiency could not be loaded." }); });
    return () => { current = false; };
  }, [stockRequestKey, focusedSkuId, trendGlobalQuery, trendEligibleAgentIds, stockRetry]);

  const detailGlobalQuery = useMemo(() => {
    if (!appliedQuery) return null;
    const query = new URLSearchParams(appliedQuery);
    for (const field of ["page", "page_size", "sort_by"]) query.delete(field);
    return query.toString();
  }, [appliedQuery]);
  const detailRequestKey = selectedPair && detailGlobalQuery
    ? `${detailGlobalQuery}|${selectedPair.agentId}|${selectedPair.productId}` : null;
  const currentDetail = detailState?.key === detailRequestKey ? detailState.result : null;
  const currentDetailError = detailError?.key === detailRequestKey ? detailError.message : null;
  useEffect(() => {
    if (!detailRequestKey || !selectedPair || !detailGlobalQuery) {
      setDetailState(null);
      setDetailError(null);
      setDetailLoading(false);
      return undefined;
    }
    let current = true;
    const query = new URLSearchParams(detailGlobalQuery);
    query.set("agent_id", selectedPair.agentId);
    query.set("product_id", selectedPair.productId);
    query.set("sales_page", String(detailSalesPage));
    query.set("sales_page_size", String(detailSalesPageSize));
    setDetailLoading(true);
    setDetailError(null);
    getJson(`/analytics/sales-velocity/detail?${query.toString()}`)
      .then((result) => { if (current) setDetailState({ key: detailRequestKey, result }); })
      .catch((error) => { if (current) setDetailError({ key: detailRequestKey, message: error.message || "Agent × SKU details could not be loaded." }); })
      .finally(() => { if (current) setDetailLoading(false); });
    return () => { current = false; };
  }, [detailRequestKey, detailGlobalQuery, selectedPair, detailSalesPage, detailSalesPageSize, detailRetry]);

  function selectAgentSku(agentId, productId) {
    setSelectedPair({ agentId: String(agentId), productId: String(productId) });
    setDetailSalesPage(1);
    setFocusedSkuId(String(productId));
    setTrendMode("agents");
    setTrendModeExplicit(true);
    setTrendAgentIds((current) => {
      const next = new Set(current);
      if (!next.has(String(agentId))) {
        if (next.size >= 8) next.delete([...next][0]);
        next.add(String(agentId));
      }
      return next;
    });
  }

  async function retrySkuSummary() {
    if (!appliedQuery) return;
    setSkuSummaryRetryLoading(true);
    setSkuSummaryRetryError(null);
    try {
      const result = await getJson(`/analytics/sales-velocity?${appliedQuery}`);
      if (result.skuSummaryError) throw new Error(result.skuSummaryError);
      setSkuSummaryRetryData({ key: appliedQuery, rows: result.skuSummary || [] });
    } catch (error) {
      setSkuSummaryRetryError(error.message || "SKU movement summary could not be loaded.");
    } finally {
      setSkuSummaryRetryLoading(false);
    }
  }

  function focusSummarySku(productId) {
    const nextProductId = String(productId);
    setFocusedSkuId(nextProductId);
    setSelectedPair((current) => current ? { ...current, productId: nextProductId } : null);
    setDetailSalesPage(1);
  }

  async function changeAgentSort(value) {
    setAgentSort(value);
    if (appliedQuery) {
      const query = new URLSearchParams(appliedQuery);
      query.set("sort_by", value);
      query.set("page", "1");
      await loadAnalytics(query);
    }
  }

  return (
    <div className="sales-velocity-page">
      <header className="sv-page-header">
        <div>
          <DashboardSwitcher />
          <h1>Sales Velocity Analysis</h1>
          <p>Compare SKU sales movement across Agents and time periods.</p>
        </div>
      </header>

      <section className="sv-filter-panel" aria-label="Sales velocity filters">
        <div className="sv-filter-grid">
          <label className="sv-filter-field">
            <span className="sv-filter-label">Reporting period</span>
            <select aria-label="Reporting period" value={period} onChange={(event) => updatePeriod(event.target.value)}>
              {DATE_PRESETS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          {period === "custom" && <>
            <label className="sv-filter-field"><span className="sv-filter-label">From</span><input aria-label="From date" type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
            <label className="sv-filter-field"><span className="sv-filter-label">To</span><input aria-label="To date" type="date" value={to} onChange={(event) => setTo(event.target.value)} /></label>
          </>}
          <label className="sv-filter-field sv-agent-scope">
            <span className="sv-filter-label">Agent Scope</span>
            <select aria-label="Agent Scope" value={scope} onChange={(event) => changeScope(event.target.value)}>
              {AGENT_SCOPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}{["active", "noSales"].includes(value) ? " — Available when analytics is applied" : ""}</option>)}
            </select>
          </label>
          <MultiSelect label="Agents" options={visibleAgentOptions} selected={selectedAgents} onChange={setSelectedAgents} searchPlaceholder="Search agents..." testId="agent-multiselect" compactCount disabled={agentControlDisabled} disabledSummary={agentUnavailableSummary} />
          {scope === "geography" && GEO_LEVELS.map(([field, label, collection], index) => {
            const source = geo[collection];
            const parentField = index > 0 ? GEO_LEVELS[index - 1][0] : null;
            const parentId = parentField ? geoSelection[parentField] : null;
            const parentMap = { district_id: "state_id", block_id: "district_id", panchayat_id: "block_id", village_id: "panchayat_id" };
            const rows = index === 0 ? source : source.filter((item) => String(item[parentMap[field]]) === String(parentId));
            return <label className="sv-filter-field" key={field}>
              <span className="sv-filter-label">{label}</span>
              <select aria-label={label} value={geoSelection[field]} onChange={(event) => updateGeo(field, event.target.value)} disabled={index > 0 && !parentId}>
                <option value="">All {label.toLowerCase()}s</option>
                {rows.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>;
          })}
          <label className="sv-filter-field">
            <span className="sv-filter-label">Category</span>
            <select aria-label="Category" value={category} onChange={(event) => {
              const next = event.target.value;
              setCategory(next);
              const allowed = new Set(products.filter((product) => !next || product.category === next).map((product) => String(product.id)));
              setSelectedSkus((current) => new Set([...current].filter((id) => allowed.has(id))));
            }}>
              <option value="">All categories</option>
              {categories.map((name) => <option value={name} key={name}>{name}</option>)}
            </select>
          </label>
          <MultiSelect label="SKUs" options={skuOptions} selected={selectedSkus} onChange={setSelectedSkus} searchPlaceholder="Search products or SKU..." testId="sku-multiselect" />
        </div>
        {scope === "active" && <p className="sv-muted sv-scope-note">Active-in-period Agent population is unavailable until analytics is applied.</p>}
        {scope === "noSales" && <p className="sv-muted sv-scope-note">No-sales Agent population is unavailable until analytics is applied.</p>}
        {scope === "geography" && <div className="sv-scope-note" role="status">
          <p className="sv-muted">{mappingLoading ? "Loading current Agent village mappings…" : agentMappingError || (selectedGeoVillages.length ? `${geographyAgents.length} Agents match the selected FPO-mapped geography.` : "Choose a geography level to narrow Agents using their current village mappings.")}</p>
          {agentMappingError && <button type="button" className="sv-inline-action" onClick={retryAgentMappings}>Retry geography load</button>}
        </div>}
        {loadError && <p className="sv-error" role="alert">{loadError}</p>}
        {dateError && <p className="sv-error" role="alert">{dateError}</p>}
        <div className="sv-filter-actions">
          <span className="sv-muted">{loading ? "Loading current filter data…" : `${agents.length} Agents · ${products.length} Products`}</span>
          <div><button type="button" className="sv-button sv-button-secondary" onClick={resetFilters} disabled={analyticsLoading}>Reset</button><button type="button" className="sv-button sv-button-primary" onClick={applyFilters} disabled={analyticsLoading || (scope === "geography" && (mappingLoading || Boolean(agentMappingError)))}>{analyticsLoading ? "Applying…" : "Apply"}</button></div>
        </div>
        {analyticsLoading && <p className="sv-muted" role="status">Loading Sales Velocity analytics…</p>}
        {analyticsError && <div className="sv-error" role="alert"><span>{analyticsError.message}</span><button type="button" className="sv-inline-action" onClick={applyFilters} disabled={analyticsLoading}>Retry</button></div>}
      </section>

      {appliedContext && <div className="sv-applied-context" aria-live="polite">
        <strong>Applied filters</strong><span>{appliedContext.period}</span>
        {appliedContext.period === "Custom" && <span>{appliedContext.from} – {appliedContext.to}</span>}
        {appliedContext.scope === "all" && <span>{appliedContext.agentCount} of {appliedContext.matchingAgentCount} Agents selected</span>}
        {appliedContext.scope === "geography" && <span>{appliedContext.agentCount} Agents in Geography</span>}
        {appliedContext.scope === "custom" && <span>{appliedContext.agentCount} Agents selected</span>}
        {appliedContext.scope === "active" && <span>Active in Period</span>}
        {appliedContext.scope === "noSales" && <span>No Sales in Period</span>}
        <span>{appliedContext.category}</span>
        {appliedContext.skuCount > 0 && <span>{appliedContext.skuCount} SKUs</span>}
      </div>}

      <section className="sv-kpi-grid" aria-label="Sales velocity summary">
        {kpis.map(([title, value, detail]) => <article className="sv-kpi-card" key={title}><span>{title}</span><strong>{analyticsLoading ? "…" : value}</strong><small>{detail}</small></article>)}
      </section>
      {analytics && Number(analytics.kpis.active_agents) === 0 && <p className="sv-muted sv-empty-state" role="status">No valid Sales found for the selected filters.</p>}

      <div className="sv-analytics-grid">
        <AnalyticsPlaceholder title="Agent × SKU Velocity" className="sv-heatmap" loading={contextLoading}>
          <div className="sv-heatmap-legend" aria-label="Relative velocity legend">
            <strong>Relative velocity within each SKU</strong>
            <span className="sv-legend-item"><i className="sv-swatch high" />High</span>
            <span className="sv-legend-item"><i className="sv-swatch middle" />Middle</span>
            <span className="sv-legend-item"><i className="sv-swatch low" />Low</span>
            <span className="sv-legend-item"><i className="sv-swatch equal" />Equal movement</span>
            <span className="sv-legend-item"><i className="sv-swatch unavailable" />Unavailable</span>
            <small>Colours compare Agents within each selected SKU for the applied filters.</small>
          </div>
          {!appliedSkuIds.length ? <div className="sv-empty-state" role="status"><p>Select one or more SKUs to compare Agent sales velocity.</p></div>
            : analytics && totalHeatmapAgents === 0 ? <div className="sv-empty-state" role="status"><p>No Agents match the applied filters.</p></div>
              : <div className="sv-heatmap-scroll" role="region" aria-label="Agent by SKU sales velocity" tabIndex="0">
                <table className="sv-heatmap-table" aria-label="Agent by SKU sales velocity">
                  <thead><tr><th className="sv-agent-sticky" scope="col">Agent</th>{heatmapProducts.map((product) => <th scope="col" key={product.id} className={selectedPair?.productId === String(product.id) ? "sv-column-selected" : ""} title={`${product.name || product.sku || `Product ${product.id}`} · ${product.sku || "—"} · ${product.unit || "—"}`}><span>{product.name || product.sku || `Product ${product.id}`}</span><small>{product.sku || "—"} · {product.unit || "—"}</small></th>)}</tr></thead>
                  <tbody>{pageAgentIds.map((agentId) => {
                    const agent = agentById.get(String(agentId));
                    const isSelectedAgent = selectedPair?.agentId === String(agentId);
                    return <tr key={agentId} className={isSelectedAgent ? "sv-row-selected" : ""}>
                      <th className="sv-agent-sticky" scope="row" title={agent?.name || `Agent ${agentId}`}>{agent?.name || `Agent ${agentId}`}</th>
                      {heatmapProducts.map((product) => {
                        const row = rowByPair.get(`${agentId}:${product.id}`);
                        const band = row ? velocityBand(row.calendar_velocity, benchmarkByProduct.get(String(product.id))) : "unavailable";
                        const isSelected = isSelectedAgent && selectedPair?.productId === String(product.id);
                        const detail = row && [`Agent: ${agent?.name || `Agent ${agentId}`}`, `SKU: ${product.name || product.sku || product.id}`, `SKU code: ${product.sku || "—"}`, `Units Sold: ${formatQuantity(row.units_sold)}`, `Calendar Days: ${row.calendar_days}`, `Calendar Velocity: ${formatVelocity(row.calendar_velocity)} units/day`, `Relative movement: ${{ high: "High", middle: "Middle", low: "Low", equal: "Equal movement", unavailable: "Unavailable" }[band]}`, `Last Sale: ${row.last_sale_date || "None in period"}`].join("\n");
                        return <td key={product.id} className={selectedPair?.productId === String(product.id) ? "sv-column-selected" : ""}><button type="button" className={`sv-heat-cell sv-band-${band}${isSelected ? " is-selected" : ""}`} disabled={!row} title={detail || "Unavailable"} aria-label={detail?.replaceAll("\n", ", ") || `Unavailable for ${agent?.name || `Agent ${agentId}`} and ${product.name || product.sku}`} aria-pressed={Boolean(isSelected)} onClick={() => {
                          selectAgentSku(agentId, product.id);
                        }}>{row ? formatVelocity(row.calendar_velocity) : "—"}</button></td>;
                      })}
                    </tr>;
                  })}</tbody>
                </table>
              </div>}
          {appliedSkuIds.length > 0 && <div className="sv-heatmap-controls">
            <label>Sort Agents <select aria-label="Sort Agents" value={agentSort} onChange={(event) => changeAgentSort(event.target.value)}><option value="agent_name">Name (A–Z)</option><option value="agent_id">Agent ID</option></select></label>
            <span>{totalHeatmapAgents} matching Agents</span>
            <label>Rows per page <select aria-label="Agent rows per page" value={heatmapPageSize} onChange={(event) => requestAnalyticsPage(1, Number(event.target.value))} disabled={!analytics || analyticsLoading}><option value="10">10</option><option value="20">20</option><option value="50">50</option></select></label>
            <div className="sv-pagination"><button type="button" onClick={() => requestAnalyticsPage(heatmapPage - 1)} disabled={!analytics || analyticsLoading || heatmapPage <= 1}>Previous</button><span>Page {analytics ? heatmapPage : "—"} of {analytics ? heatmapPageCount : "—"}</span><button type="button" onClick={() => requestAnalyticsPage(heatmapPage + 1)} disabled={!analytics || analyticsLoading || heatmapPage >= heatmapPageCount}>Next</button></div>
          </div>}
        </AnalyticsPlaceholder>
        <AnalyticsPlaceholder title="SKU Sales Trend" className="sv-trend-panel" loading={contextLoading}>
          {!appliedSkuIds.length ? <div className="sv-empty-state" role="status"><p>Select a SKU to view its sales velocity trend.</p></div> : <>
            <div className="sv-trend-controls">
              <label>Focused SKU <select aria-label="Focused SKU" value={focusedSkuId || appliedSkuIds[0]} onChange={(event) => focusSummarySku(event.target.value)}>
                {heatmapProducts.map((product) => <option key={product.id} value={String(product.id)}>{product.name || product.sku || `Product ${product.id}`}</option>)}
              </select></label>
              <small className="sv-muted">Focused SKU applies to Trend, Stock Efficiency, Agent Ranking and Details.</small>
              <div className="sv-trend-toggle" role="group" aria-label="Trend granularity">{[["day", "Daily"], ["week", "Weekly"], ["month", "Monthly"]].map(([value, label]) => <button key={value} type="button" aria-pressed={trendGranularity === value} onClick={() => setTrendGranularity(value)}>{label}</button>)}</div>
              <div className="sv-trend-toggle" role="group" aria-label="Trend mode">{[["combined", "Selected Agents Combined"], ["agents", "Compare Agents"]].map(([value, label]) => <button key={value} type="button" aria-pressed={trendMode === value} onClick={() => { setTrendMode(value); setTrendModeExplicit(true); }}>{label}</button>)}</div>
            </div>
            {trendMode === "agents" && <div className="sv-trend-series-row">
              <label className="sv-trend-search-label">Trend Agents <input aria-label="Search trend Agents" placeholder="Search Agents..." value={trendAgentSearch} onChange={(event) => setTrendAgentSearch(event.target.value)} /></label>
              <div className="sv-trend-series-options" aria-label="Trend Agent series">
                {trendAgentOptions.map((agent) => {
                  const id = String(agent.id); const checked = trendAgentIds.has(id); const disabled = !checked && trendAgentIds.size >= 8;
                  return <label key={id} className={disabled ? "is-disabled" : ""}><input aria-label={`Trend series ${agent.name}`} type="checkbox" checked={checked} disabled={disabled} onChange={() => setTrendAgentIds((current) => { const next = new Set(current); if (checked) next.delete(id); else next.add(id); return next; })} />{agent.name || `Agent ${id}`}</label>;
                })}
              </div>
              <small>{trendAgentIds.size} selected · maximum 8</small>
            </div>}
            {trendEligibleAgentIds.length === 0 ? <div className="sv-empty-state" role="status"><p>No Agents match the applied filters.</p></div>
              : trendMode === "agents" && trendAgentIds.size === 0 ? <div className="sv-empty-state" role="status"><p>Choose one or more Agents to compare, up to 8.</p></div>
                : trendLoading ? <p className="sv-chart-status" role="status">Loading sales velocity trend…</p>
                  : trendError ? <div className="sv-error" role="alert"><span>{trendError}</span><button type="button" className="sv-inline-action" onClick={() => setTrendRetry((current) => current + 1)}>Retry trend</button></div>
                    : trendData?.series?.length ? <VelocityTrendChart series={trendData.series} granularity={trendGranularity} />
                      : <div className="sv-empty-state" role="status"><p>No Agent series are available for this trend.</p></div>}
          </>}
        </AnalyticsPlaceholder>
        <AnalyticsPlaceholder title="Stock Efficiency" className="sv-stock-panel" loading={contextLoading}>
          {!appliedSkuIds.length ? <div className="sv-empty-state" role="status"><p>Select a SKU to analyse stock efficiency.</p></div>
            : trendEligibleAgentIds.length === 0 ? <div className="sv-empty-state" role="status"><p>No Agents match the applied filters.</p></div>
              : currentStockError ? <div className="sv-error" role="alert"><span>{currentStockError}</span><button type="button" className="sv-inline-action" onClick={() => { setStockError(null); setStockRetry((value) => value + 1); }}>Retry stock efficiency</button></div>
                : !currentStockData ? <p className="sv-chart-status" role="status">Loading stock efficiency…</p>
                  : <>
                    <p className="sv-stock-summary"><strong>{currentStockData.product.name}</strong> · {currentStockData.population.stock_recorded_agents} Agents plotted · Stock unavailable for {currentStockData.population.stock_unavailable_agents} Agents</p>
                    {currentStockData.population.stock_recorded_agents === 0
                      ? <div className="sv-empty-state" role="status"><p>Current operational stock is unavailable for the selected Agents.</p></div>
                      : <StockEfficiencyChart data={currentStockData} selectedAgentId={selectedPair?.productId === focusedSkuId ? selectedPair.agentId : null} onSelect={(agentId) => selectAgentSku(agentId, focusedSkuId)} />}
                  </>}
          <p className="sv-stock-help">Current operational stock now is compared with average sales velocity for the selected period, not historical closing stock. Days of Cover = current operational stock ÷ selected-period calendar velocity.</p>
          <p className="sv-stock-help">Stock uses the current operational Agent balance and may require reconciliation with stock ledger/batch records.</p>
        </AnalyticsPlaceholder>
        <AnalyticsPlaceholder title="Performance Ranking" className="sv-performance-ranking" loading={contextLoading}>
          <PerformanceRanking view={rankingView} setView={setRankingView} rankingOrder={rankingOrder} setRankingOrder={setRankingOrder}
            rankingLimit={rankingLimit} setRankingLimit={setRankingLimit}
            rankingPoints={currentStockData?.points || null} rankingLoading={!currentStockData && !currentStockError}
            rankingError={currentStockError} onRetryRanking={() => { setStockError(null); setStockRetry((value) => value + 1); }}
            agents={agents} selectedAgentId={selectedPair?.productId === String(focusedProductId) ? selectedPair.agentId : null}
            focusedSku={focusedProduct} onSelectAgent={(agentId) => selectAgentSku(agentId, focusedProductId)}
            skuRows={skuSummaryRows} skuSummaryError={skuSummaryError} skuSummaryLoading={skuSummaryRetryLoading}
            onRetrySkuSummary={retrySkuSummary} selectedSkuId={focusedProductId} onFocusSku={focusSummarySku} />
        </AnalyticsPlaceholder>
        <AnalyticsPlaceholder title="Agent × SKU Details" className="sv-details" loading={contextLoading}>
          {!selectedPair ? <div className="sv-empty-state" role="status"><p>Select an Agent × SKU combination to view details.</p></div>
            : <>
              <p className="sv-detail-heading"><strong>{currentDetail?.agent?.name || agentById.get(selectedPair.agentId)?.name || `Agent ${selectedPair.agentId}`} × {currentDetail?.product?.name || productById.get(selectedPair.productId)?.name || productById.get(selectedPair.productId)?.sku || `Product ${selectedPair.productId}`}</strong>
                {currentDetail?.product?.sku && <small>{currentDetail.product.sku}{currentDetail.product.unit ? ` · ${currentDetail.product.unit}` : ""}</small>}</p>
              {detailLoading && !currentDetail ? <div className="sv-chart-status" role="status">Loading Agent × SKU details…</div>
                : currentDetailError ? <div className="sv-error-state" role="alert"><p>{currentDetailError}</p><button type="button" className="sv-button sv-button-secondary" onClick={() => setDetailRetry((value) => value + 1)}>Retry details</button></div>
                  : currentDetail && <>
                    <div className="sv-detail-grid">
                      <div><span>SKU Units Sold</span><strong>{formatQuantity(currentDetail.summary.unitsSold)} {currentDetail.product.unit || "units"}</strong></div>
                      <div><span>Calendar Velocity</span><strong>{formatVelocity(currentDetail.summary.calendarVelocity)} / day</strong></div>
                      <div><span>Current Operational Stock</span><strong className={currentDetail.summary.stockStatus === "negative_balance" ? "sv-negative-stock" : ""}>{currentDetail.summary.stockStatus === "unavailable" ? "Unavailable" : `${formatQuantity(currentDetail.summary.currentStock)} ${currentDetail.product.unit || "units"}${currentDetail.summary.stockStatus === "negative_balance" ? " · Negative balance" : ""}`}</strong></div>
                      <div><span>Days of Cover</span><strong>{displayCover(currentDetail.summary)}</strong></div>
                      <div><span>Last Sale</span><strong>{currentDetail.summary.lastSaleDate || "No sales in period"}</strong></div>
                    </div>
                    <section className="sv-detail-section" aria-labelledby="sv-daily-title">
                      <h3 id="sv-daily-title">Daily Sales Movement</h3>
                      <DetailMovementChart days={currentDetail.dailyMovement} unit={currentDetail.product.unit} />
                      <small className="sv-muted">Bars show SKU units sold each day; they do not represent historical stock.</small>
                    </section>
                    <section className="sv-detail-section" aria-labelledby="sv-sales-title">
                      <h3 id="sv-sales-title">Recent Sales Transactions</h3>
                      {!currentDetail.sales.totalRows ? <div className="sv-empty-state" role="status"><p>No matching Sales in the selected period.</p></div> : <>
                        <div className="sv-detail-table-scroll"><table className="sv-detail-table" aria-label="Recent Sales Transactions"><thead><tr><th>Invoice / Sale ID</th><th>Invoice Date</th><th>Customer</th><th>SKU Quantity</th><th>Unit Price</th><th>Line Amount</th><th>Payment Status</th></tr></thead>
                          <tbody>{currentDetail.sales.rows.map((sale) => <tr key={sale.salesItemId}><td>{sale.invoiceNo || sale.saleId}</td><td>{sale.invoiceDate}</td><td>{sale.customerName || "—"}</td><td>{formatQuantity(sale.quantity)} {currentDetail.product.unit || "units"}</td><td>{formatCurrency(sale.unitPrice)}</td><td>{formatCurrency(sale.lineAmount)}</td><td>{sale.paymentStatus}</td></tr>)}</tbody></table></div>
                        <div className="sv-detail-pagination"><label>Rows per page <select aria-label="Sales rows per page" value={detailSalesPageSize} onChange={(event) => { setDetailSalesPageSize(Number(event.target.value)); setDetailSalesPage(1); }}><option value="10">10</option><option value="20">20</option><option value="50">50</option></select></label>
                          <div className="sv-pagination"><button type="button" onClick={() => setDetailSalesPage((value) => Math.max(1, value - 1))} disabled={detailLoading || detailSalesPage <= 1}>Previous</button><span>Page {currentDetail.sales.page} of {Math.max(1, Math.ceil(currentDetail.sales.totalRows / currentDetail.sales.pageSize))}</span><button type="button" onClick={() => setDetailSalesPage((value) => value + 1)} disabled={detailLoading || detailSalesPage >= Math.max(1, Math.ceil(currentDetail.sales.totalRows / currentDetail.sales.pageSize))}>Next</button></div>
                        </div>
                      </>}
                    </section>
                    <section className="sv-detail-section" aria-labelledby="sv-allocations-title">
                      <h3 id="sv-allocations-title">Recent Stock Allocations</h3>
                      {!currentDetail.allocations.rows.length ? <div className="sv-empty-state" role="status"><p>No recent stock allocations for this Agent × SKU.</p></div> : <div className="sv-detail-table-scroll"><table className="sv-detail-table" aria-label="Recent Stock Allocations"><thead><tr><th>Allocation</th><th>Date</th><th>Allocated Quantity</th><th>Status</th><th>Accepted / Rejected</th></tr></thead><tbody>{currentDetail.allocations.rows.map((allocation) => <tr key={allocation.allocationId}><td>{allocation.allocationNo || allocation.allocationId}</td><td>{allocation.allocationDate}</td><td>{formatQuantity(allocation.allocatedQuantity)} {currentDetail.product.unit || "units"}</td><td>{allocation.status}{allocation.status === "PENDING" && allocation.requiresAgentConfirmation ? " · Awaiting Agent confirmation" : ""}</td><td>{allocation.acceptedAt || allocation.rejectedAt || "—"}</td></tr>)}</tbody></table></div>}
                      <p className="sv-stock-help">Allocation history is shown for context; current stock comes from the operational Agent balance. Pending allocations are not treated as stock received by the Agent.</p>
                    </section>
                  </>}
            </>}
        </AnalyticsPlaceholder>
      </div>
    </div>
  );
}

export { dateRangeFor, indiaToday, MultiSelect };
